import { mainApi } from "services/main";
import {
  SearchDocumentRequest,
  SearchDocumentResponse,
  GetDocumentUniqueValuesRequest,
  GetDocumentUniqueValuesResponse,
  BatchRegisterDocumentsInput,
  BatchRegisterDocumentsOutput,
  BatchUpdateDocumentsInput,
  BatchUpdateDocumentsOutput,
  BatchDeleteDocumentsInput,
  BatchDeleteDocumentsOutput,
  GetDocumentInput,
  GetDocumentOutput,
  CreateTeamFilesImportInput,
  CreateTeamFilesImportOutput,
  CreateTeamFilesExportInput,
  CreateTeamFilesExportOutput,
  DownloadUrlRequest,
  DownloadUrlResponse,
  UploadUrlRequest,
  UploadUrlResponse,
  GetDocumentsKbStatusRequest,
  GetDocumentsKbStatusResponse,
} from "./definitions";
import { encodeLastUriComponent } from "utils/url-utils";
import { invalidateDocumentsCache } from "./utils";

const extendedApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    searchDocuments: builder.query<
      SearchDocumentResponse,
      SearchDocumentRequest
    >({
      query: (params) => ({
        url: "/documents/search",
        method: "POST",
        body: params,
      }),
      providesTags: ["Documents"],
    }),
    getDocumentUniqueValues: builder.query<
      GetDocumentUniqueValuesResponse,
      GetDocumentUniqueValuesRequest
    >({
      query: (params) => ({
        url: "/documents/unique-values",
        method: "POST",
        body: params,
      }),
      providesTags: ["Documents"],
    }),
    batchRegisterDocuments: builder.mutation<
      BatchRegisterDocumentsOutput,
      BatchRegisterDocumentsInput
    >({
      query: (params) => ({
        url: "/documents/register",
        method: "POST",
        body: params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await invalidateDocumentsCache(dispatch, queryFulfilled);
      },
    }),
    batchUpdateDocuments: builder.mutation<
      BatchUpdateDocumentsOutput,
      BatchUpdateDocumentsInput
    >({
      query: (params) => ({
        url: "/documents/update",
        method: "POST",
        body: params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await invalidateDocumentsCache(dispatch, queryFulfilled);
      },
    }),
    batchDeleteDocuments: builder.mutation<
      BatchDeleteDocumentsOutput,
      BatchDeleteDocumentsInput
    >({
      query: (params) => ({
        url: "/documents/delete",
        method: "POST",
        body: params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await invalidateDocumentsCache(dispatch, queryFulfilled);
      },
    }),
    getDocument: builder.query<GetDocumentOutput, GetDocumentInput>({
      query: (params) => ({
        url: `/documents/${params.documentId}`,
        method: "GET",
      }),
      providesTags: ["Documents"],
    }),
    createTeamFilesImport: builder.mutation<
      CreateTeamFilesImportOutput,
      CreateTeamFilesImportInput
    >({
      query: (params) => ({
        url: "/team-files/import",
        method: "POST",
        body: params,
      }),
    }),
    createTeamFilesExport: builder.mutation<
      CreateTeamFilesExportOutput,
      CreateTeamFilesExportInput
    >({
      query: (params) => ({
        url: "/team-files/export",
        method: "POST",
        body: params,
      }),
    }),
    downloadUrl: builder.query<DownloadUrlResponse, DownloadUrlRequest>({
      query: (request: DownloadUrlRequest) => ({
        url: `documents/download-url/${encodeLastUriComponent(request.documentId)}`,
        params: {
          preview: request.preview,
          ...(request.versionId && { versionId: request.versionId }),
          ...(request.fileIndex !== undefined && {
            fileIndex: request.fileIndex,
          }),
        },
      }),
    }),
    uploadUrl: builder.mutation<UploadUrlResponse, UploadUrlRequest>({
      query: (request: UploadUrlRequest) => ({
        url: "file/upload-url",
        body: request,
        method: "POST",
      }),
    }),
    getDocumentsKbStatus: builder.query<
      GetDocumentsKbStatusResponse,
      GetDocumentsKbStatusRequest
    >({
      query: (params) => ({
        url: "/documents/kb-status",
        method: "POST",
        body: params,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchDocumentsQuery,
  useLazySearchDocumentsQuery,
  useGetDocumentUniqueValuesQuery,
  useBatchRegisterDocumentsMutation,
  useBatchUpdateDocumentsMutation,
  useBatchDeleteDocumentsMutation,
  useGetDocumentQuery,
  useLazyGetDocumentQuery,
  useCreateTeamFilesImportMutation,
  useCreateTeamFilesExportMutation,
  useDownloadUrlQuery,
  useLazyDownloadUrlQuery,
  useUploadUrlMutation,
  useGetDocumentsKbStatusQuery,
  useLazyGetDocumentsKbStatusQuery,
} = extendedApi;
